import 'swiper/swiper-bundle.css';

import cn from 'classnames';
import * as React from 'react';
import { useEffect, useMemo, useRef } from 'react';
import Swiper from 'swiper';
import {
  A11y,
  Autoplay,
  EffectCards,
  EffectCoverflow,
  EffectCreative,
  EffectCube,
  EffectFade,
  EffectFlip,
  Navigation,
  Pagination,
  Scrollbar,
  Thumbs
} from 'swiper/modules';
import { SwiperOptions } from 'swiper/types';

import styles from './styles.module.scss';

// eslint-disable-next-line react-hooks/rules-of-hooks
Swiper.use([
  Navigation,
  Pagination,
  Scrollbar,
  A11y,
  Autoplay,
  Thumbs,
  EffectFade,
  EffectCube,
  EffectCoverflow,
  EffectFlip,
  EffectCards,
  EffectCreative
]);

export interface CarouselProps {
  slidesPerView?: string;
  slidesPerGroup?: number;
  spaceBetween?: number;
  speed?: number;
  navigation?: boolean;
  pagination?: boolean;
  loop?: boolean;
  autoplay?: boolean;
  autoplaySpeed?: number;
  rtl?: boolean;
  centeredSlides?: boolean;
  breakpoints?: {
    [width: number]:
      | {
          slidesPerView: string;
          slidesPerGroup: number;
          spaceBetween?: string;
        }
      | undefined;
  };
  effect?: string;
  themeColor?: string;
  className?: string;
  children?: React.ReactElement[];
}

const Carousel: React.FC<CarouselProps> = ({
  slidesPerView = '3',
  slidesPerGroup = 1,
  spaceBetween = 16,
  speed = 300,
  navigation = true,
  pagination = true,
  loop = false,
  autoplay = false,
  autoplaySpeed = 3000,
  rtl = false,
  breakpoints,
  effect = 'slide',
  themeColor,
  centeredSlides = false,
  className = '',
  ...props
}) => {
  const ref = useRef<HTMLDivElement>(null);
  const slider = useRef<Swiper | null>(null);
  const isInitialized = useRef(false);

  const breakpointsKey = useMemo(() => {
    if (!breakpoints) return 'none';
    return JSON.stringify(breakpoints);
  }, [breakpoints]);

  useEffect(() => {
    if (isInitialized.current) return;

    const timeout = setTimeout(() => {
      const wrapper = ref.current?.querySelector('.wp-block-erom-carousel');
      const ell = ref.current?.querySelector('.swiper');
      if (wrapper && ell) {
        const numericSlidesPerView = slidesPerView != 'auto' ? parseInt(slidesPerView) : 'auto';
        const slideSpeed = Math.round(
          (speed || 1) / (typeof numericSlidesPerView === 'number' ? numericSlidesPerView : 1)
        );
        const totalSlides = ell.querySelectorAll('.swiper-slide').length;

        const breakpointsObject: { [width: number]: SwiperOptions } | undefined =
          breakpoints &&
          typeof breakpoints === 'object' &&
          !Array.isArray(breakpoints) &&
          Object.keys(breakpoints).length > 0
            ? Object.entries(breakpoints).reduce(
                (acc, [width, breakpoint]) => {
                  if (!breakpoint) return acc;

                  const numericWidth = parseInt(width, 10);
                  const numericBreakpointSlidesPerView =
                    breakpoint.slidesPerView != 'auto' ? parseInt(breakpoint.slidesPerView) : 'auto';

                  acc[numericWidth] = {
                    slidesPerView: numericBreakpointSlidesPerView,
                    speed: Math.min(
                      Math.round(
                        (typeof numericBreakpointSlidesPerView === 'number' ? numericBreakpointSlidesPerView : 1) *
                          slideSpeed
                      ),
                      speed
                    ),
                    spaceBetween: breakpoint.spaceBetween || spaceBetween
                  };

                  if (!loop) {
                    acc[numericWidth].slidesPerGroup = breakpoint.slidesPerGroup;
                  }

                  return acc;
                },
                {} as { [width: number]: SwiperOptions }
              )
            : undefined;

        const desiredLoopSlides = loop && slidesPerView !== 'auto' ? 2 * parseInt(slidesPerView) : 0;
        const loopAdditionalSlides = loop ? Math.min(desiredLoopSlides, totalSlides) : 0;

        const options: SwiperOptions = {
          slidesPerView: slidesPerView != 'auto' ? parseInt(slidesPerView) : 'auto',
          centeredSlides,
          spaceBetween,
          speed,
          loopAdditionalSlides,
          navigation: navigation && {
            nextEl: rtl
              ? (wrapper.querySelector('.swiper-button-prev') as HTMLElement)
              : (wrapper.querySelector('.swiper-button-next') as HTMLElement),
            prevEl: rtl
              ? (wrapper.querySelector('.swiper-button-next') as HTMLElement)
              : (wrapper.querySelector('.swiper-button-prev') as HTMLElement)
          },
          pagination: pagination && {
            el: wrapper.querySelector('.swiper-pagination') as HTMLElement,
            clickable: true
          },
          autoHeight: true,
          loop,
          effect,
          freeMode: slidesPerView == 'auto',
          cssMode: slidesPerView == 'auto',
          autoplay: autoplay
            ? {
                delay: autoplaySpeed,
                disableOnInteraction: false,
                pauseOnMouseEnter: true,
                waitForTransition: true
              }
            : false,
          grabCursor: true,
          breakpoints: breakpointsObject
        };

        if (!loop) {
          options.slidesPerGroup = slidesPerGroup;
        }

        slider.current = new Swiper(ell as HTMLElement, options);
        isInitialized.current = true;
      }
    }, 100);

    return () => {
      clearTimeout(timeout);
      if (slider.current) {
        slider.current.destroy();
        slider.current = null;
        isInitialized.current = false;
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    autoplay,
    autoplaySpeed,
    breakpointsKey,
    effect,
    loop,
    navigation,
    pagination,
    rtl,
    centeredSlides,
    slidesPerGroup,
    slidesPerView,
    spaceBetween,
    speed
  ]);

  useEffect(() => {
    const resizeObserver = new ResizeObserver(() => {
      slider.current?.updateAutoHeight();
    });

    if (ref.current) {
      resizeObserver.observe(ref.current);
    }

    return () => {
      resizeObserver.disconnect();
    };
  }, []);

  useEffect(() => {
    const handleResize = () => {
      if (slider.current) {
        slider.current.update();
      }
    };

    window.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  return (
    <div ref={ref}>
      <style>
        {`body {
          ${themeColor ? `--swiper-theme-color: ${themeColor};` : ''}
          direction: ${rtl ? 'rtl' : 'ltr'};
        }`}
      </style>
      <div
        className={cn(className, styles.swiper, {
          [styles.loop]: loop
        })}
        dir={rtl ? 'ltr' : 'auto'}
        {...props}
      />
    </div>
  );
};

export default Carousel;
